// NodeAboutForms.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Hooks
import useNodeForms from 'node/hooks/useNodeForms';

// Services
import { DEFAULT_FORM_KEY } from 'services/forms/FormModel';

// Utilities
import { buildVisibleFormsForSettings } from 'collection/settings/forms/formSettingsUtils';
import {
  getNodeFormScopeMemoryKey,
  getStoredFormKeyForScope,
  setStoredFormKeyForScope,
} from 'node/forms/lastNodeFormMemory';

// CSS
import './NodeAboutForms.css';

// Components
import FormRenderer from 'form/FormRenderer';
import Icon from 'svg/Icon';
import NodeDetails from 'node/details/NodeDetails';

function normalizeString(value) {
  return String(value || '').trim();
}

const ABOUT_FALLBACK_EXCLUDED_FIELD_TYPES = ['cover', 'comments', 'activity'];

function isDefaultForm(form) {
  return Boolean(form?.isDefault) || normalizeString(form?.key) === DEFAULT_FORM_KEY;
}

function formHasPersistedLayout(form) {
  return Boolean(
    form?.layout
    && form?.blocksByKey
    && Object.keys(form.blocksByKey).length,
  );
}

/**
 * NodeAboutForms
 *
 * Workspace About surface: FormRenderer for the selected template, chips when
 * 2+ forms exist, Details as default with last-choice memory. Falls back to
 * NodeDetails when the selected form has no persisted layout yet.
 */
const NodeAboutForms = React.memo(({
  node,
  readOnly = false,
  onCoverSaved,
  onLocationSaved,
  onNodeChange,
  onSubtitleSaved,
  onTagsSaved,
  excludedFieldTypes = ABOUT_FALLBACK_EXCLUDED_FIELD_TYPES,
  autoFocusRichText = false,
  autoFocusKey = '',
}) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  const baseKey = normalizeString(node?.baseKey);
  const typeKey = normalizeString(node?.type);

  // ----- HOOKS ----- //

  const { forms, defaultForm, isReady } = useNodeForms({
    typeKey,
    baseKey,
  });

  // ----- STATE ----- //

  const [selectedFormKey, setSelectedFormKey] = useState('');

  // ----- MEMOS ----- //

  const scopeMemoryKey = useMemo(
    () => getNodeFormScopeMemoryKey({ baseKey, typeKey }),
    [baseKey, typeKey],
  );

  const visibleForms = useMemo(
    () => buildVisibleFormsForSettings(forms),
    [forms],
  );

  const showChips = visibleForms.length > 1;

  const selectedForm = useMemo(() => {
    const cleaned = normalizeString(selectedFormKey);
    if (!cleaned) return visibleForms[0] || null;
    return visibleForms.find((form) => form.key === cleaned) || visibleForms[0] || null;
  }, [selectedFormKey, visibleForms]);

  const hasPersistedLayout = formHasPersistedLayout(selectedForm);

  // ----- EFFECTS ----- //

  useEffect(() => {
    setSelectedFormKey('');
  }, [scopeMemoryKey]);

  useEffect(() => {
    if (!isReady || !visibleForms.length) {
      setSelectedFormKey('');
      return;
    }

    const hasCurrentSelection = visibleForms.some((form) => form.key === selectedFormKey);
    if (hasCurrentSelection) return;

    const rememberedKey = getStoredFormKeyForScope(scopeMemoryKey);
    const rememberedExists = rememberedKey
      && visibleForms.some((form) => form.key === rememberedKey);

    if (rememberedExists) {
      setSelectedFormKey(rememberedKey);
      return;
    }

    const preferredKey = normalizeString(defaultForm?.key)
      || visibleForms.find(isDefaultForm)?.key
      || visibleForms[0]?.key
      || '';
    setSelectedFormKey(preferredKey);
  }, [defaultForm?.key, isReady, scopeMemoryKey, selectedFormKey, visibleForms]);

  // ----- HANDLERS ----- //

  const handleSelectForm = useCallback((formKey) => {
    const cleaned = normalizeString(formKey);
    if (!cleaned) return;
    setSelectedFormKey(cleaned);
    setStoredFormKeyForScope(scopeMemoryKey, cleaned);
  }, [scopeMemoryKey]);

  // ----- RENDER ----- //

  if (!isReady) return null;

  return (
    <div className="node-about-forms">
      {showChips ? (
        <div className="node-about-forms-chips">
          <div className="node-about-forms-chips-scroller">
            {visibleForms.map((form) => {
              const formKey = normalizeString(form?.key);
              const isActive = formKey === normalizeString(selectedForm?.key);
              const label = normalizeString(form?.title) || 'Untitled Form';
              const iconKey = isDefaultForm(form) ? 'dynamic_form' : 'edit_note';

              return (
                <button
                  key={formKey}
                  type="button"
                  className={`node-about-forms-chip${isActive ? ' active' : ''}`}
                  style={{
                    backgroundColor: isActive ? theme.backgroundMuted : theme.panel,
                    color: isActive ? theme.foreground : theme.foregroundMuted,
                  }}
                  title={normalizeString(form?.description) || label}
                  onClick={() => handleSelectForm(formKey)}
                >
                  <Icon
                    iconKey={iconKey}
                    width={16}
                    height={16}
                    color={isActive ? theme.foreground : theme.foregroundMuted}
                  />
                  <span className="node-about-forms-chip-label">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="node-about-forms-content">
        {hasPersistedLayout ? (
          <FormRenderer
            formDoc={selectedForm}
            node={node}
            previewMode={readOnly}
            onChange={readOnly ? undefined : onNodeChange}
            onCoverSaved={readOnly ? undefined : onCoverSaved}
            onLocationSaved={readOnly ? undefined : onLocationSaved}
            onSubtitleSaved={readOnly ? undefined : onSubtitleSaved}
            onTagsSaved={readOnly ? undefined : onTagsSaved}
          />
        ) : (
          <NodeDetails
            node={node}
            readOnly={readOnly}
            onCoverSaved={readOnly ? undefined : onCoverSaved}
            onLocationSaved={readOnly ? undefined : onLocationSaved}
            onNodeChange={readOnly ? undefined : onNodeChange}
            onSubtitleSaved={readOnly ? undefined : onSubtitleSaved}
            onTagsSaved={readOnly ? undefined : onTagsSaved}
            excludedFieldTypes={excludedFieldTypes}
            autoFocusRichText={!readOnly && autoFocusRichText}
            autoFocusKey={autoFocusKey}
          />
        )}
      </div>
    </div>
  );
});

NodeAboutForms.displayName = 'NodeAboutForms';

export default NodeAboutForms;
